import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import OnboardingChecklist from '../components/OnboardingChecklist';
import RiskOverview from '../components/dashboard/RiskOverview';
import AdaptationWidget from '../components/dashboard/AdaptationWidget';
import CustomerSupportSessionWidget from '../components/dashboard/CustomerSupportSessionWidget';
import ParticipantDashboard from './ParticipantDashboard';
import AssignerDashboard from './AssignerDashboard';
import SuperAdminDashboard from './SuperAdminDashboard';
import { Card, CardContent } from '../components/ui/card';
import {
  Users, Target, Calendar, AlertCircle, Clock, ArrowRight,
  TriangleAlert, CalendarCheck, CheckCircle,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const ALLVARLIGHET_LABEL = { lag: 'Låg', medel: 'Medel', hog: 'Hög' };
const ALLVARLIGHET_COLOR = {
  lag:  'bg-blue-100 text-blue-700',
  medel:'bg-yellow-100 text-yellow-800',
  hog:  'bg-red-100 text-red-700',
};

// ── Shared label style ─────────────────────────────────────────────────────────
const SL = 'text-xs font-bold uppercase tracking-wide text-slate-500';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats]       = useState(null);
  const [activities, setActivities] = useState(null);
  const [followUps, setFollowUps]   = useState([]);
  const [warnings, setWarnings]     = useState([]);
  const [myDay, setMyDay]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [superAdminHasSession, setSuperAdminHasSession] = useState(null);

  const showMyDay = user && user.role !== 'super_admin';

  // Determine whether super_admin has an active support session
  useEffect(() => {
    if (user?.role !== 'super_admin') { setSuperAdminHasSession(false); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/admin/support-sessions/my-active');
        if (!cancelled) setSuperAdminHasSession(Array.isArray(r.data) && r.data.length > 0);
      } catch {
        if (!cancelled) setSuperAdminHasSession(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.role]);

  useEffect(() => {
    if (user?.role === 'super_admin' && !superAdminHasSession) return;
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, superAdminHasSession]);

  const fetchDashboardData = async () => {
    try {
      const requests = [
        api.get('/dashboard/stats'),
        api.get('/dashboard/activities'),
        api.get('/dashboard/follow-ups'),
        api.get('/dashboard/warnings'),
      ];
      if (user && user.role !== 'super_admin') requests.push(api.get('/dashboard/my-day'));
      const [statsRes, activitiesRes, followUpsRes, warningsRes, myDayRes] = await Promise.all(requests);
      setStats(statsRes.data);
      setActivities(activitiesRes.data);
      setFollowUps(followUpsRes.data || []);
      setWarnings(warningsRes.data || []);
      if (myDayRes) setMyDay(myDayRes.data);
    } catch {
      toast.error('Kunde inte hämta dashboard-data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' });
  };

  const todayLabel = new Date().toLocaleDateString('sv-SE', {
    weekday: 'long', day: 'numeric', month: 'long',
  }).replace(/^./, c => c.toUpperCase());

  // Role-specific dashboards
  if (user?.role === 'read_only_participant') return <ParticipantDashboard />;
  if (user?.role === 'read_only_assigner')   return <AssignerDashboard />;
  if (user?.role === 'super_admin' && superAdminHasSession === false) return <SuperAdminDashboard />;
  if (user?.role === 'super_admin' && superAdminHasSession === null)  return <SuperAdminDashboard />;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-page">

        {/* ── 1. Header ──────────────────────────────────────────────────────── */}
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Översikt av systemet</p>
        </div>

        {/* GDPR supportläge-widget (customer_admin only) */}
        {user?.role === 'customer_admin' && <CustomerSupportSessionWidget />}

        {/* ── 2. Min dag ─────────────────────────────────────────────────────── */}
        {showMyDay && (
          <div data-testid="my-day-section">
            <div className="mb-4">
              <p className={SL}>MIN DAG</p>
              <p className="text-sm text-slate-400 mt-0.5">{todayLabel}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              {/* AKTIVITETER */}
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className={SL}>AKTIVITETER</span>
                    <CalendarCheck size={15} className="text-slate-400" />
                  </div>
                  <p className="text-3xl font-bold text-slate-800">
                    {myDay?.my_participants_count ?? '—'}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    {myDay
                      ? `${myDay.my_participants_count} deltagare · ${myDay.todays_meetings?.length ?? 0} möten idag`
                      : 'Laddar...'}
                  </p>
                  {myDay?.todays_meetings?.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {myDay.todays_meetings.slice(0, 3).map(m => (
                        <div
                          key={m.id}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => navigate(`/participants/${m.participant_id}`)}
                        >
                          <Clock size={12} className="text-slate-400 shrink-0" />
                          <span className="text-slate-700 text-xs truncate">{m.rubrik}</span>
                          {m.participant_name && (
                            <span className="text-slate-400 text-xs ml-auto shrink-0">{m.participant_name}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => navigate('/participants')}
                    className="text-xs text-slate-500 hover:underline flex items-center gap-1 mt-3"
                  >
                    Se alla deltagare <ArrowRight size={11} />
                  </button>
                </CardContent>
              </Card>

              {/* UPPFÖLJNINGAR */}
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className={SL}>UPPFÖLJNINGAR</span>
                    <Clock size={15} className="text-slate-400" />
                  </div>
                  {!myDay ? (
                    <p className="text-sm text-slate-500">Laddar...</p>
                  ) : myDay.participants_to_follow_up.length === 0 ? (
                    <>
                      <p className="text-3xl font-bold text-slate-800">0</p>
                      <p className="text-sm text-slate-500 mt-1">Inga uppföljningar idag</p>
                    </>
                  ) : (
                    <>
                      <p className="text-3xl font-bold text-slate-800">
                        {myDay.participants_to_follow_up.length}
                      </p>
                      <p className="text-sm text-slate-500 mt-1">förfaller idag</p>
                      <div className="mt-3 space-y-1.5">
                        {myDay.participants_to_follow_up.slice(0, 3).map(fu => (
                          <div
                            key={fu.id}
                            className="flex items-start gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                            onClick={() => navigate('/follow-ups')}
                          >
                            {fu.overdue
                              ? <AlertCircle size={13} className="text-red-500 shrink-0 mt-0.5" />
                              : <Clock size={13} className="text-slate-400 shrink-0 mt-0.5" />
                            }
                            <span className={`text-xs truncate ${fu.overdue ? 'text-red-600 font-medium' : 'text-slate-700'}`}>
                              {fu.beskrivning}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  <button
                    onClick={() => navigate('/follow-ups')}
                    className="text-xs text-slate-500 hover:underline flex items-center gap-1 mt-3"
                  >
                    Se alla uppföljningar <ArrowRight size={11} />
                  </button>
                </CardContent>
              </Card>

              {/* AVVIKELSER */}
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className={SL}>AVVIKELSER</span>
                    <TriangleAlert size={15} className="text-slate-400" />
                  </div>
                  {!myDay ? (
                    <p className="text-sm text-slate-500">Laddar...</p>
                  ) : myDay.new_deviations.length === 0 ? (
                    <p className="text-sm text-slate-500">Inga nya avvikelser</p>
                  ) : (
                    <div>
                      <p className="text-sm font-semibold text-slate-800 truncate">
                        {myDay.new_deviations[0].participant_name || 'Okänd deltagare'}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ALLVARLIGHET_COLOR[myDay.new_deviations[0].allvarlighetsgrad] || 'bg-gray-100 text-gray-600'}`}>
                          {ALLVARLIGHET_LABEL[myDay.new_deviations[0].allvarlighetsgrad] || '–'}
                        </span>
                        {myDay.new_deviations.length > 1 && (
                          <span className="text-xs text-slate-400">+{myDay.new_deviations.length - 1} till</span>
                        )}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => navigate('/deviations')}
                    className="text-xs text-slate-500 hover:underline flex items-center gap-1 mt-3"
                  >
                    Se alla avvikelser <ArrowRight size={11} />
                  </button>
                </CardContent>
              </Card>

            </div>
          </div>
        )}

        {/* ── 3. KPI-kort ────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { id: '0', label: 'TOTALT DELTAGARE', value: stats?.total_participants ?? 0, icon: <Users size={20} /> },
            { id: '1', label: 'AKTIVA MÅL',       value: stats?.active_goals       ?? 0, icon: <Target size={20} /> },
            { id: '2', label: 'KUNDER',            value: stats?.total_customers    ?? 0, icon: <Calendar size={20} /> },
          ].map(kpi => (
            <Card key={kpi.id} className="border border-slate-200 shadow-sm" data-testid={`stat-card-${kpi.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <p className={SL}>{kpi.label}</p>
                  <span className="text-slate-400">{kpi.icon}</span>
                </div>
                <p className="text-3xl font-bold text-slate-800 mt-3">
                  {loading ? '—' : kpi.value}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ── 4. Onboarding ──────────────────────────────────────────────────── */}
        <OnboardingChecklist />

        {/* ── 5. Deltagare som kräver uppmärksamhet ──────────────────────────── */}
        <RiskOverview />

        {/* ── 6. Anpassningar att hantera ────────────────────────────────────── */}
        <AdaptationWidget />

        {/* ── 7. Akuta uppföljningar ─────────────────────────────────────────── */}
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <p className={SL}>AKUTA UPPFÖLJNINGAR</p>
              <button
                className="text-xs text-slate-500 hover:text-slate-700 hover:underline flex items-center gap-1"
                onClick={() => navigate('/follow-ups')}
              >
                Se alla <ArrowRight size={11} />
              </button>
            </div>

            {loading ? (
              <p className="text-sm text-slate-500">Laddar...</p>
            ) : followUps.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-1">
                <CheckCircle size={14} className="text-green-500 shrink-0" />
                Inga akuta uppföljningar
              </div>
            ) : (
              <div className="space-y-2">
                {followUps.map(fu => (
                  <div key={fu.id} className="flex items-start justify-between gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 leading-snug truncate">{fu.beskrivning}</p>
                      {fu.participant_name && (
                        <p className="text-xs text-slate-500 mt-0.5">{fu.participant_name}</p>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      {fu.deadline && (
                        <span className={`flex items-center gap-1 text-xs font-medium ${fu.overdue ? 'text-red-500' : 'text-slate-500'}`}>
                          {fu.overdue ? <AlertCircle size={11} /> : <Clock size={11} />}
                          {formatDate(fu.deadline)}
                        </span>
                      )}
                      <span className={`mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        fu.status === 'klar'     ? 'bg-green-100 text-green-700'  :
                        fu.status === 'pagaende' ? 'bg-blue-100 text-blue-700'   :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {fu.status === 'klar' ? 'Klar' : fu.status === 'pagaende' ? 'Pågående' : 'Ej påbörjad'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* System warnings (participants without recent meetings) */}
        {!loading && warnings.length > 0 && (
          <Card className="border border-red-200 bg-red-50/40" data-testid="warnings-widget">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <TriangleAlert size={15} className="text-red-500 shrink-0" />
                <p className="text-xs font-bold uppercase tracking-wide text-red-600">Systemvarningar</p>
              </div>
              <div className="space-y-2">
                {warnings.map(w => (
                  <div
                    key={w.participant_id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => navigate(`/participants/${w.participant_id}`)}
                    data-testid={`warning-row-${w.participant_id}`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{w.participant_name}</p>
                      <p className="text-xs text-slate-500">
                        {w.days_since_last_meeting == null
                          ? 'Inget möte registrerat'
                          : `Inget möte på ${w.days_since_last_meeting} dagar`}
                      </p>
                    </div>
                    <ArrowRight size={14} className="text-slate-400 shrink-0" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── 8. Senaste deltagare + Senaste avvikelser ──────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          <Card className="border border-slate-200 shadow-sm" data-testid="recent-participants-card">
            <CardContent className="p-5">
              <p className={`${SL} mb-4`}>SENASTE DELTAGARE</p>
              {loading ? (
                <p className="text-sm text-slate-500">Laddar...</p>
              ) : activities?.recent_participants?.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {activities.recent_participants.slice(0, 5).map(p => (
                    <div key={p.id} className="flex items-center justify-between py-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p>
                        <p className="text-xs text-slate-500 truncate">{p.email || 'Ingen e-post'}</p>
                      </div>
                      <span className={`ml-3 shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${
                        p.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {p.status === 'active' ? 'Aktiv' : p.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Inga deltagare ännu</p>
              )}
            </CardContent>
          </Card>

          <Card className="border border-slate-200 shadow-sm" data-testid="recent-deviations-card">
            <CardContent className="p-5">
              <p className={`${SL} mb-4`}>SENASTE AVVIKELSER</p>
              {loading ? (
                <p className="text-sm text-slate-500">Laddar...</p>
              ) : activities?.recent_deviations?.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {activities.recent_deviations.map(dev => (
                    <div key={dev.id} className="flex items-center justify-between py-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">
                          {dev.participant_name || dev.participant_id}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{dev.typ}</p>
                      </div>
                      <span className={`ml-3 shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${
                        dev.allvarlighetsgrad === 'hog'  ? 'bg-red-100 text-red-700'      :
                        dev.allvarlighetsgrad === 'medel'? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {ALLVARLIGHET_LABEL[dev.allvarlighetsgrad] || dev.allvarlighetsgrad}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-green-600 py-1">
                  <CheckCircle size={14} className="shrink-0" />
                  Inga öppna avvikelser
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
